import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../services/api';
import SEO from '../components/SEO';
import { 
  User, 
  Mail, 
  Phone, 
  GraduationCap, 
  BookOpen, 
  Calendar, 
  Shield, 
  ShoppingBag, 
  Building2, 
  Award,
  ExternalLink,
  Loader2,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';

const Avatar = ({ name, size = 'large' }) => {
  const initial = (name && name[0]) ? name[0].toUpperCase() : '?';
  const sizeClasses = size === 'large' 
    ? 'w-24 h-24 text-4xl sm:w-28 sm:h-28 sm:text-5xl' 
    : 'w-12 h-12 text-xl sm:w-14 sm:h-14 sm:text-2xl';
  return (
    <div
      className={`flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 ${sizeClasses} font-bold text-white shadow-xl shadow-indigo-500/20 ring-4 ring-gray-800/50`}
    >
      {initial}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color = 'indigo' }) => {
  const colorClasses = {
    indigo: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400',
    green: 'bg-green-500/10 border-green-500/20 text-green-400',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  };
  
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-4 hover:bg-gray-900/60 transition-all">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg border ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-400 font-medium truncate">{label}</p>
          <p className="text-sm font-semibold text-white mt-0.5 truncate">{value}</p>
        </div>
      </div>
    </div>
  );
};

const InfoCard = ({ icon: Icon, title, children, className = '' }) => {
  return (
    <div className={`rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-5 sm:p-6 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className="p-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
          <Icon className="w-4 h-4 text-indigo-400" />
        </div>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
};

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [purchases, setPurchases] = useState([]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API.AUTH_PROFILE, { withCredentials: true });
        if (res.data && res.data.success) {
          setUser(res.data.user);
          setPurchases(res.data.purchases || []);
        } else {
          setError(res.data?.message || 'Failed to load profile');
        }
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || 'Failed to load profile'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-300 text-lg font-medium">Loading your profile...</p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 px-4">
        <div className="text-center max-w-md">
          <div className="p-3 rounded-full bg-red-500/10 border border-red-500/20 w-fit mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Error Loading Profile</h2>
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );

  const fullName = `${user?.firstname || ''} ${user?.lastname || ''}`.trim();
  const purchaseItems = purchases.flatMap((p) => {
    const items = [];
    if (p.branchId)
      p.branchId.forEach((b) =>
        items.push({ type: 'Branch', title: `${b.year} ${b.branch}`, id: b._id, driveId: b.googleDriveId, orderId: p.orderId })
      );
    if (p.subjectId)
      p.subjectId.forEach((s) =>
        items.push({ type: 'Subject', title: s.title, id: s._id, driveId: s.googleDriveId, orderId: p.orderId })
      );
    if (p.unitId)
      p.unitId.forEach((u) =>
        items.push({ type: 'Unit', title: u.title, id: u._id, driveId: u.googleDriveId, orderId: p.orderId })
      );
    return items;
  });

  return (
    <>
      <SEO 
        title="My Profile - ReferMe"
        description="View your profile and manage your account settings."
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Profile Header */}
        <div className="relative overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-xl p-6 sm:p-8 mb-8 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="flex justify-center sm:justify-start">
              <Avatar name={user?.firstname || user?.email} size="large" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                {fullName || user?.email}
              </h1>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-800/60 border border-gray-700 text-sm text-gray-300">
                  <Mail className="w-3.5 h-3.5" />
                  <span className="truncate max-w-[200px] sm:max-w-none">{user?.email}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-800/60 border border-gray-700 text-sm text-gray-300">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Member since {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-sm font-medium">
                  <Award className="w-3.5 h-3.5" />
                  <span>{user?.role || 'User'}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={ShoppingBag} 
            label="Total Purchases" 
            value={purchases.length} 
            color="indigo"
          />
          <StatCard 
            icon={BookOpen} 
            label="Items Owned" 
            value={purchaseItems.length} 
            color="purple"
          />
          <StatCard 
            icon={Shield} 
            label="Account Status" 
            value="Verified" 
            color="green"
          />
          <StatCard 
            icon={Award} 
            label="Role" 
            value={user?.role || 'User'} 
            color="indigo"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Personal Info */}
          <div className="space-y-6 lg:col-span-1">
            <InfoCard icon={User} title="Personal Information">
              <dl className="space-y-4">
                <div>
                  <dt className="flex items-center gap-2 text-xs font-medium text-gray-400 mb-1">
                    <User className="w-3.5 h-3.5" />
                    Full Name
                  </dt>
                  <dd className="text-sm font-medium text-white pl-5">{fullName || '—'}</dd>
                </div>
                <div>
                  <dt className="flex items-center gap-2 text-xs font-medium text-gray-400 mb-1">
                    <Mail className="w-3.5 h-3.5" />
                    Email Address
                  </dt>
                  <dd className="text-sm font-medium text-white pl-5 break-all">{user?.email || '—'}</dd>
                </div>
                {user?.phone && (
                  <div>
                    <dt className="flex items-center gap-2 text-xs font-medium text-gray-400 mb-1">
                      <Phone className="w-3.5 h-3.5" />
                      Phone Number
                    </dt>
                    <dd className="text-sm font-medium text-white pl-5">{user.phone}</dd>
                  </div>
                )}
              </dl>
            </InfoCard>

            <InfoCard icon={Shield} title="Account Details">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-800/30 border border-gray-700/50">
                  <span className="text-sm text-gray-300">Verification Status</span>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span className="text-sm font-medium text-green-400">Verified</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-800/30 border border-gray-700/50">
                  <span className="text-sm text-gray-300">Account Role</span>
                  <span className="text-sm font-medium text-indigo-400">{user?.role || 'User'}</span>
                </div>
              </div>
            </InfoCard>
          </div>

          {/* Right Column - Education & Purchases */}
          <div className="space-y-6 lg:col-span-2">
            <InfoCard icon={GraduationCap} title="Education Details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-gray-800/30 border border-gray-700/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-indigo-400" />
                    <dt className="text-xs font-medium text-gray-400">College</dt>
                  </div>
                  <dd className="text-sm font-medium text-white">{user?.college || '—'}</dd>
                </div>
                <div className="p-4 rounded-lg bg-gray-800/30 border border-gray-700/50">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-4 h-4 text-indigo-400" />
                    <dt className="text-xs font-medium text-gray-400">Branch</dt>
                  </div>
                  <dd className="text-sm font-medium text-white">{user?.branch || '—'}</dd>
                </div>
                <div className="p-4 rounded-lg bg-gray-800/30 border border-gray-700/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-indigo-400" />
                    <dt className="text-xs font-medium text-gray-400">Year</dt>
                  </div>
                  <dd className="text-sm font-medium text-white">{user?.year || '—'}</dd>
                </div>
              </div>
            </InfoCard>

            <InfoCard icon={ShoppingBag} title="My Purchases">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-400">
                  {purchaseItems.length > 0 
                    ? `${purchaseItems.length} item${purchaseItems.length > 1 ? 's' : ''} across ${purchases.length} order${purchases.length > 1 ? 's' : ''}`
                    : 'No purchases yet'
                  }
                </p>
              </div>
              {purchaseItems.length === 0 ? (
                <div className="text-center py-12">
                  <div className="p-4 rounded-full bg-gray-800/30 border border-gray-700/50 w-fit mx-auto mb-4">
                    <ShoppingBag className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="text-gray-400 text-sm">You haven't made any purchases yet.</p>
                  <p className="text-gray-500 text-xs mt-1">Start exploring our content to get started!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {purchaseItems.map((item) => {
                    const typeColors = {
                      Branch: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
                      Subject: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
                      Unit: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400',
                    };
                    
                    return (
                      <a
                        key={`${item.orderId}-${item.id}`}
                        href={
                          item.driveId
                            ? item.type === 'Unit'
                              ? `https://drive.google.com/file/d/${item.driveId}/view`
                              : `https://drive.google.com/drive/folders/${item.driveId}`
                            : '#'
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative block p-5 bg-gradient-to-br from-gray-800/40 to-gray-900/40 border border-gray-700/50 rounded-xl hover:border-indigo-500/50 hover:shadow-xl hover:shadow-indigo-500/10 transition-all duration-300 overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/0 to-purple-500/0 group-hover:from-indigo-500/5 group-hover:to-purple-500/5 transition-all duration-300" />
                        <div className="relative">
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex flex-col gap-1.5">
                              <span className={`w-fit px-2.5 py-1 rounded-lg text-xs font-semibold tracking-wide border ${typeColors[item.type] || typeColors.Unit}`}>
                                {item.type}
                              </span>
                            </div>
                            <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-indigo-400 transition-colors flex-shrink-0" />
                          </div>
                          <h4 className="font-semibold text-white text-sm mb-2 group-hover:text-indigo-300 transition-colors line-clamp-2">
                            {item.title}
                          </h4>
                          <p className="text-xs text-gray-400 font-mono">Order #{item.orderId}</p>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
            </InfoCard>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Profile;
